import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import {Provider, SystemCode, TranslationRule} from '@helpers/types/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlarisEditPanelService, EditPanelInputData } from '@campaign-portal/components-library';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslationRulesService } from '../translation-rules.service';
import { SystemCodesService } from "../../system-codes/system-codes.service";
import {ProvidersService} from "@helpers/repo/providers.service";
import {AdditionalValidators} from "@helpers/shared/validators";

@Component({
	selector: 'app-edit-translation-rules',
	templateUrl: './edit-rule.component.html',
	styleUrl: './edit-rule.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EditTranslationRulesComponent implements OnInit {
	readonly errors: { key: string; value: string }[] = [
		{key: 'mask', value: 'gl.errors.invalid_format'},
		{key: 'required', value: 'gl.errors.required'}];
	readonly rule: TranslationRule = {
		id: null,
		name: null,
		status: false,
		sourceType: null,
		responseCodes: null,
		systemCode: null,
	};
	readonly form = new FormGroup({
		id: new FormControl<number | null>(null),
		name: new FormControl<string | null>(null, [Validators.required]),
		status: new FormControl<boolean>(false),
		sourceType: new FormControl<string | null>(null, [Validators.required]),
		responseCodes: new FormControl<string | null>(null,
			[Validators.required, AdditionalValidators.uniqueValuesArrayValidator()]),
		systemCode: new FormControl<string | null>(null, [Validators.required])
	});
	readonly providersFilter = new FormControl();
	providers: Provider[] = [];

	readonly systemCodeFilter = new FormControl();
	codes: SystemCode[] = [];

	readonly separator = ', ';
	private readonly destroyRef = inject(DestroyRef);
	
	constructor(
			@Inject(EditPanelInputData) private readonly inputData: { rule?: TranslationRule },
			public readonly service: TranslationRulesService,
			private readonly editPanel: AlarisEditPanelService,
			public providersService: ProvidersService,
			public readonly codesService: SystemCodesService
	) {
		if ( this.inputData.rule ) {
			this.rule = this.inputData.rule;
		}
	}
	
	ngOnInit(): void {
		this.reset();
		this.providersService.loadProviders('').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
		this.providersService.list$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((list) => {
			this.providersFilter.reset();
			this.providers = list;
		});
		this.codesService.list$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((codes) => {
			if (codes) {
				this.codes = codes;
			}
			this.systemCodeFilter.reset();
		});
		this.providersFilter.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.providers = this.providersService.list.filter((provider) =>
					provider?.name.toLowerCase()
						.includes(value?.toLowerCase() ?? '')
				);
			});
		this.systemCodeFilter.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.codes = this.codesService.list.filter((code) =>
					code?.value?.toLowerCase()
						.includes(value?.toLowerCase() ?? '') || code?.description?.toLowerCase()
						.includes(value?.toLowerCase() ?? '')
				);
			});
	}
	
	reset(): void {
		const rule: TranslationRule = structuredClone(this.rule);
		this.form.reset(rule);
	}
	
	save(): void {
		const rule = {
			id: this.form.controls.id.value,
			name: this.form.controls.name.value,
			status: this.form.controls.status.value,
			sourceType: this.form.controls.sourceType.value,
			responseCodes: this.form.controls.responseCodes.value,
			systemCodeId: this.getSystemCodeIDByValue(this.form.controls.systemCode.value)
		};
		this.service.update(rule)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				if (resp?.success || resp?.createdId) {
					this.editPanel.close(true);
				}
			});
	}
	
	close(): void {
		this.editPanel.close(false);
	}

	getSystemCodeIDByValue(val: string | null): number | null {
		return this.codesService.list.filter((code) =>
			code?.value === val)[0].id
	}
}
